import { Checkbox } from "@/shared/components/ui/checkbox"
import { Badge } from "@/shared/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/components/ui/collapsible"
import { ChevronRight } from "lucide-react"
import type { BuiltinToolInfo } from '@/modules/agent/types/tool'

export interface ToolListItem {
  id: string
  name: string
  server_name?: string
}

interface ToolSelectorProps {
  tools: ToolListItem[]
  builtinTools?: BuiltinToolInfo[]
  selectedTools: string[]
  onToolsChange: (tools: string[]) => void
  isLoading?: boolean
}

/**
 * 工具选择器组件
 * 
 * 功能：
 * - 按 MCP server_name 分组展示工具
 * - 支持按组全选/取消
 * - 支持单个工具勾选
 * - 显示已选择数量
 * 
 * 适用于：基础 Agent、Structured Agent、Companion Agent 等所有节点类型
 */
export function ToolSelector({ tools, builtinTools = [], selectedTools, onToolsChange, isLoading }: ToolSelectorProps) {
  // 将builtin tools转换为统一格式
  const builtinToolsAsItems: ToolListItem[] = builtinTools.map(tool => ({
    id: tool.name,
    name: tool.name,
    server_name: `内置工具 (${tool.category})`
  }))
  
  // 合并MCP工具和builtin工具
  const allTools = [...tools, ...builtinToolsAsItems]
  
  // 按 server_name 分组工具
  const groupedTools = allTools.reduce((acc, tool) => {
    const groupName = tool.server_name || '其他工具'
    if (!acc[groupName]) {
      acc[groupName] = []
    }
    acc[groupName].push(tool)
    return acc
  }, {} as Record<string, ToolListItem[]>)

  const handleGroupSelectAll = (groupTools: ToolListItem[], checked: boolean) => {
    const groupToolIds = groupTools.map(t => t.id)
    let newTools: string[]
    if (checked) {
      // 全选：添加该组所有工具
      newTools = [...new Set([...selectedTools, ...groupToolIds])]
    } else {
      // 取消全选：移除该组所有工具
      newTools = selectedTools.filter((id: string) => !groupToolIds.includes(id))
    }
    onToolsChange(newTools)
  }

  const handleToolToggle = (toolId: string, checked: boolean) => {
    const newTools = checked
      ? [...selectedTools, toolId]
      : selectedTools.filter((id: string) => id !== toolId)
    onToolsChange(newTools)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
        加载工具列表...
      </div>
    )
  }

  if (tools.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        暂无可用工具，请先在项目设置中配置可用工具
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {Object.entries(groupedTools).map(([groupName, groupTools]) => {
        const selectedInGroup = groupTools.filter(t => selectedTools.includes(t.id)).length
        const allSelected = selectedInGroup === groupTools.length
        const someSelected = selectedInGroup > 0 && selectedInGroup < groupTools.length

        return (
          <Collapsible key={groupName} defaultOpen className="border rounded-lg group/collapsible">
            <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded-t-lg">
              <div className="flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 text-slate-400 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                <span className="font-medium text-[13px]">{groupName}</span>
                <Badge variant="secondary" className="text-[11px] px-1.5 py-0 h-5 ml-1">
                  {selectedInGroup}/{groupTools.length}
                </Badge>
              </div>
              <div 
                className="flex items-center gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={(checked) => handleGroupSelectAll(groupTools, checked === true)}
                  className="h-4 w-4 data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground"
                />
                <span className="text-[11px] text-slate-500">全选</span>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="border-t">
              <div className="p-3 space-y-2">
                {groupTools.map((tool) => (
                  <div key={tool.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`tool-${tool.id}`}
                      checked={selectedTools.includes(tool.id)}
                      onCheckedChange={(checked) => handleToolToggle(tool.id, checked === true)}
                    />
                    <label
                      htmlFor={`tool-${tool.id}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {tool.name}
                    </label>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )
      })}
      <p className="text-xs text-slate-500">
        已选择 {selectedTools.length} 个工具
      </p>
    </div>
  )
}
